import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Database,
  Download,
  Upload,
  Trash2,
  Plus,
  Clock,
  Save,
} from 'lucide-react';
import { backupApi, BackupEntry, BackupScheduleConfig } from '@/api/endpoints';
import { toast } from '@/utils/toast';
import { format } from 'date-fns';
import { ConfirmationModal } from '@azyntor-labs/nexus-ui';

const formatSize = (bytes: number) =>
  bytes > 1e6 ? `${(bytes / 1e6).toFixed(1)} MB` : `${(bytes / 1e3).toFixed(1)} KB`;

export const BackupManagementPage = () => {
  const queryClient = useQueryClient();
  const [createModal, setCreateModal] = useState(false);
  const [createDesc, setCreateDesc] = useState('');
  const [confirmAction, setConfirmAction] = useState<{ type: 'restore' | 'delete'; id: string } | null>(null);

  const { data: backupsRes, isLoading } = useQuery({
    queryKey: ['backups'],
    queryFn: () => backupApi.getBackups(),
    staleTime: 30000,
  });

  const backups: BackupEntry[] = Array.isArray(backupsRes?.data?.data) ? backupsRes.data.data : [];

  const { data: scheduleRes } = useQuery({
    queryKey: ['backup-schedule'],
    queryFn: () => backupApi.getSchedule(),
    staleTime: 30000,
  });

  const schedule: BackupScheduleConfig = (scheduleRes?.data?.data || {
    enabled: false,
    frequency: 'daily',
    time: '02:00',
    retention_days: 30,
    max_backups: 10,
  }) as BackupScheduleConfig;

  const [scheduleForm, setScheduleForm] = useState<BackupScheduleConfig | null>(null);
  const currentSchedule = scheduleForm || schedule;

  const createMutation = useMutation({
    mutationFn: () => backupApi.createBackup({ description: createDesc }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backups'] });
      toast.success('Backup created');
      setCreateModal(false);
      setCreateDesc('');
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Backup creation failed'),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => backupApi.restoreBackup(id),
    onSuccess: () => {
      toast.success('Backup restored');
      setConfirmAction(null);
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Restore failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => backupApi.deleteBackup(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backups'] });
      toast.success('Backup deleted');
      setConfirmAction(null);
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Delete failed'),
  });

  const updateScheduleMutation = useMutation({
    mutationFn: (config: BackupScheduleConfig) => backupApi.updateSchedule(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backup-schedule'] });
      toast.success('Schedule updated');
      setScheduleForm(null);
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Schedule update failed'),
  });

  const handleDownload = async (id: string, filename: string) => {
    try {
      const response = await backupApi.downloadBackup(id);
      const blob = new Blob([response.data as any]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-emerald-500/20 text-emerald-400';
      case 'in_progress': return 'bg-amber-500/20 text-amber-400';
      case 'failed': return 'bg-red-500/20 text-red-400';
      default: return 'bg-white/[0.06] text-white/60';
    }
  };

  const typeBadge = (type: string) => {
    switch (type) {
      case 'manual': return 'bg-teal-500/20 text-teal-400';
      case 'automatic': return 'bg-cyan-500/20 text-cyan-400';
      case 'scheduled': return 'bg-purple-500/20 text-purple-400';
      default: return 'bg-white/[0.06] text-white/60';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white/90">Backup Management</h1>
          <p className="text-sm text-white/50 mt-1">Manage database backups and recovery</p>
        </div>
        <button onClick={() => setCreateModal(true)} className="btn-primary flex items-center space-x-2 px-4 py-2 rounded-lg text-sm">
          <Plus className="w-4 h-4" />
          <span>Create Backup</span>
        </button>
      </div>

      {/* Backups table */}
      <div className="card-dark overflow-hidden">
        <div className="p-4 border-b border-white/[0.06]">
          <h3 className="text-sm font-medium text-white/90">Backups</h3>
        </div>
        {isLoading ? (
          <div className="p-8 text-center text-white/50">Loading backups...</div>
        ) : backups.length === 0 ? (
          <div className="p-8 text-center">
            <Database className="w-12 h-12 text-white/20 mx-auto mb-3" />
            <p className="text-white/60">No backups yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="p-3 text-left text-xs font-medium text-white/50 uppercase">Filename</th>
                  <th className="p-3 text-left text-xs font-medium text-white/50 uppercase">Size</th>
                  <th className="p-3 text-left text-xs font-medium text-white/50 uppercase">Type</th>
                  <th className="p-3 text-left text-xs font-medium text-white/50 uppercase">Status</th>
                  <th className="p-3 text-left text-xs font-medium text-white/50 uppercase">Created At</th>
                  <th className="p-3 text-left text-xs font-medium text-white/50 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {backups.map((backup) => (
                  <tr key={backup.id} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                    <td className="p-3">
                      <p className="text-sm text-white/90 font-mono">{backup.filename}</p>
                      {backup.description && <p className="text-xs text-white/50">{backup.description}</p>}
                    </td>
                    <td className="p-3 text-sm text-white/80">{formatSize(backup.size)}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${typeBadge(backup.type)}`}>{backup.type}</span>
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${statusBadge(backup.status)}`}>{backup.status}</span>
                    </td>
                    <td className="p-3 text-xs text-white/70">
                      {backup.created_at ? format(new Date(backup.created_at), 'MMM dd, yyyy HH:mm') : '—'}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center space-x-2">
                        <button onClick={() => handleDownload(backup.id, backup.filename)} className="text-teal-400 hover:text-teal-300" title="Download">
                          <Download className="w-4 h-4" />
                        </button>
                        <button onClick={() => setConfirmAction({ type: 'restore', id: backup.id })} className="text-amber-400 hover:text-amber-300" title="Restore">
                          <Upload className="w-4 h-4" />
                        </button>
                        <button onClick={() => setConfirmAction({ type: 'delete', id: backup.id })} className="text-red-400 hover:text-red-300" title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Schedule Section */}
      <div className="card-dark p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
            <Clock className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white/90">Backup Schedule</h2>
            <p className="text-xs text-white/50">Configure automated backup schedule</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="flex items-center justify-between col-span-full">
            <span className="text-sm text-white/70">Enabled</span>
            <button
              onClick={() => setScheduleForm({ ...currentSchedule, enabled: !currentSchedule.enabled })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${currentSchedule.enabled ? 'bg-teal-500' : 'bg-white/[0.1]'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${currentSchedule.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          <div>
            <label className="block text-sm text-white/70 mb-1">Frequency</label>
            <select
              value={currentSchedule.frequency}
              onChange={(e) => setScheduleForm({ ...currentSchedule, frequency: e.target.value as 'daily' | 'weekly' | 'monthly' })}
              className="input-dark w-full"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          <div>
            <label className="block text-sm text-white/70 mb-1">Time</label>
            <input
              type="time"
              value={currentSchedule.time}
              onChange={(e) => setScheduleForm({ ...currentSchedule, time: e.target.value })}
              className="input-dark w-full"
            />
          </div>

          <div>
            <label className="block text-sm text-white/70 mb-1">Retention (days)</label>
            <input
              type="number"
              value={currentSchedule.retention_days}
              onChange={(e) => setScheduleForm({ ...currentSchedule, retention_days: Number(e.target.value) })}
              className="input-dark w-full"
              min={1}
            />
          </div>

          <div>
            <label className="block text-sm text-white/70 mb-1">Max Backups</label>
            <input
              type="number"
              value={currentSchedule.max_backups}
              onChange={(e) => setScheduleForm({ ...currentSchedule, max_backups: Number(e.target.value) })}
              className="input-dark w-full"
              min={1}
            />
          </div>
        </div>

        {scheduleForm && (
          <div className="flex justify-end mt-6">
            <button
              onClick={() => updateScheduleMutation.mutate(scheduleForm)}
              disabled={updateScheduleMutation.isPending}
              className="btn-primary flex items-center space-x-2 px-4 py-2 rounded-lg text-sm"
            >
              <Save className="w-4 h-4" />
              <span>{updateScheduleMutation.isPending ? 'Saving...' : 'Save Schedule'}</span>
            </button>
          </div>
        )}
      </div>

      {/* Create Backup Modal */}
      {createModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setCreateModal(false)}>
          <div className="bg-[#1a1f2e] border border-white/[0.08] rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-white/90 mb-4">Create Backup</h3>
            <div>
              <label className="block text-sm text-white/70 mb-1">Description (optional)</label>
              <input type="text" value={createDesc} onChange={(e) => setCreateDesc(e.target.value)} className="input-dark w-full" placeholder="Backup description..." />
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button onClick={() => setCreateModal(false)} className="btn-outline px-4 py-2 rounded-lg text-sm">Cancel</button>
              <button onClick={() => createMutation.mutate()} disabled={createMutation.isPending} className="btn-primary px-4 py-2 rounded-lg text-sm">
                {createMutation.isPending ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Action Modal */}
      <ConfirmationModal
        isOpen={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={() => {
          if (confirmAction?.type === 'restore') restoreMutation.mutate(confirmAction.id);
          else if (confirmAction?.type === 'delete') deleteMutation.mutate(confirmAction.id);
        }}
        title={`${confirmAction?.type === 'restore' ? 'Restore' : 'Delete'} Backup?`}
        message={
          confirmAction?.type === 'restore'
            ? 'This will restore the database from this backup. Current data will be overwritten.'
            : 'This backup will be permanently deleted.'
        }
        variant="danger"
        confirmText={confirmAction?.type === 'restore' ? 'Restore' : 'Delete'}
        cancelText="Cancel"
      />
    </div>
  );
};
